import { Box, Divider } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateCalendarStyleOptions,
  updateLegendOptions,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import SwitchWithInput from '../SwitchWithInput';
import { useChartLegend } from './LegendComponents/useChartLegend';
import { LegendPositionSelect } from './LegendComponents/LegendPositionSelect.tsx';
import { OrientationRadioGroup } from './LegendComponents/OrientationRadioGroup';
import { LegendSizeControls } from './LegendComponents/LegendSizeControls';
import { RootState } from '../../../redux';
import { Bold } from 'lucide-react';

const positions = [
  { pos: 'Top Left', top: 'top', left: 'left' },
  { pos: 'Top', top: 'top', left: 'center' },
  { pos: 'Top Right', top: 'top', left: 'right' },
  { pos: 'Middle Left', top: 'middle', left: 'left' },
  { pos: 'Middle', top: 'middle', left: 'center' },
  { pos: 'Middle Right', top: 'middle', left: 'right' },
  { pos: 'Bottom Left', top: 'bottom', left: 'left' },
  { pos: 'Bottom', top: 'bottom', left: 'center' },
  { pos: 'Bottom Right', top: 'bottom', left: 'right' },
];

const orientOption = [
  { name: 'Horizontal', key: 'horizontal' },
  { name: 'Vertical', key: 'vertical' },
];
const ChartLegend = ({ iconColor }: { iconColor?: string }) => {
  const { chartControls, tabTileProps, chartProperties } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
    chartProperties: state.chartProperties,
  }));

  const dispatch = useDispatch();
  const updateLegendOption = (propKey: string, option: string, value: any) =>
    dispatch(updateLegendOptions(propKey, option, value));
  const updateCalendarStyle = (propKey: string, option: string, value: any) =>
    dispatch(updateCalendarStyleOptions(propKey, option, value));

  const { propKey, showLegend, orientation, isCalendarOrHeatmap } = useChartLegend({
    chartControls,
    tabTileProps,
    chartProperties,
  });

  const legendOptions = chartControls.properties[propKey].legendOptions;
  const calendarStyleOptions = chartControls.properties[propKey].calendarStyleOptions;

  const setOrient = (item: string) => {
    if (isCalendarOrHeatmap) {
      updateCalendarStyle(propKey, 'orientation', item);
    } else {
      updateLegendOption(propKey, 'orientation', item);
    }
  };

  const updateSelectedPosition = (selectedValue: string) => {
    const positionSelected = positions.find((pos) => pos.pos === selectedValue);
    if (positionSelected) {
      updateLegendOption(propKey, 'position', positionSelected);
    }
  };

  return (
    <div className="optionsInfo" style={{ overflowX: 'hidden' }}>
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: '0.1rem',
          marginTop: '0',
          marginBottom: '0',
        }}
      >
        <label
          htmlFor="enableDisable"
          className="enableDisableLabel"
          style={{ fontWeight: 600, color: iconColor ? iconColor : '#404040' }}
        >
          LEGEND
        </label>
        <Box
          sx={{
            width: '50px',
            height: '24px',
            display: 'flex',
            transform: 'translateX(63px)',
            marginBottom: '2px',

            '@supports (-moz-appearance: none)': {
              transform: 'translateX(56px)',
            },
          }}
        >
          <SwitchWithInput
            isChecked={showLegend}
            onSwitch={() => updateLegendOption(propKey, 'showLegend', !showLegend)}
          />
        </Box>
      </div>

      {showLegend && (
        <>
          <div
            className="optionDescription"
            style={{
              marginTop: '0',
              paddingLeft: '0.4rem',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Position
          </div>

          {legendOptions.position?.pos && (
            <LegendPositionSelect
              positions={positions}
              selectedPosition={legendOptions.position}
              onPositionChange={updateSelectedPosition}
            />
          )}

          {isCalendarOrHeatmap && (
            <>
              <div
                className="optionDescription"
                style={{
                  padding: '0 6% 5px 4%',
                  marginTop: '15px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Piecewise
              </div>
              <div
                style={{
                  display: 'flex',
                  width: '97%',
                  margin: '0 auto 0.5rem auto',
                  color: '#404040',
                }}
              >
                <div
                  style={{
                    borderRadius: '5px 0 0 5px',
                    cursor: 'pointer',
                    transition: '0.2s',
                    backgroundColor: calendarStyleOptions.pieceWise ? '#E0E0E0' : 'white',
                  }}
                  className={calendarStyleOptions.pieceWise ? 'radioButtonSelected' : 'radioButton'}
                  onClick={() => updateCalendarStyle(propKey, 'pieceWise', true)}
                >
                  ON
                </div>
                <div
                  style={{
                    borderRadius: '0 5px 5px 0',
                    cursor: 'pointer',
                    transition: '0.2s',
                    backgroundColor: !calendarStyleOptions.pieceWise ? '#E0E0E0' : 'white',
                  }}
                  className={
                    !calendarStyleOptions.pieceWise ? 'radioButtonSelected' : 'radioButton'
                  }
                  onClick={() => updateCalendarStyle(propKey, 'pieceWise', false)}
                >
                  OFF
                </div>
              </div>
            </>
          )}
          <div
            className="optionDescription"
            style={{
              marginTop: '5px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Orientation
          </div>
          <OrientationRadioGroup
            orientation={orientation}
            options={orientOption}
            onChange={setOrient}
          />

          <div
            className="optionDescription"
            style={{
              paddingBottom: '0px',
              marginTop: '0',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Size
          </div>

          <div style={{ paddingBottom: '15px' }}>
            <LegendSizeControls
              isCalendarOrHeatmap={isCalendarOrHeatmap}
              pieceWise={calendarStyleOptions.pieceWise}
              orientation={orientation}
              itemGap={legendOptions.itemGap}
              width={isCalendarOrHeatmap ? calendarStyleOptions.width : 0}
              height={isCalendarOrHeatmap ? calendarStyleOptions.height : 0}
              symbolWidth={isCalendarOrHeatmap ? undefined : legendOptions.symbolWidth}
              symbolHeight={isCalendarOrHeatmap ? undefined : legendOptions.symbolHeight}
              onItemGapChange={(value) => updateLegendOption(propKey, 'itemGap', value)}
              onWidthChange={(value) => updateCalendarStyle(propKey, 'width', value)}
              onHeightChange={(value) => updateCalendarStyle(propKey, 'height', value)}
              onSymbolWidthChange={(value) => updateLegendOption(propKey, 'symbolWidth', value)}
              onSymbolHeightChange={(value) => updateLegendOption(propKey, 'symbolHeight', value)}
              iconColor={iconColor}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ChartLegend;
